import type { BookingStatusType } from '../services/bookingApi';

const MIN_CANCEL_HOURS = 24;

/**
 * Check if a booking can be cancelled/refunded.
 * CANCELLED_BY_HOST: always true (user picks refund method, no 24h gate)
 * CONFIRMED: only if >24h before session start
 */
export function canCancelBooking(booking: {
  status: string;
  session: { startTime: string };
}): boolean {
  const upper = booking.status.toUpperCase();
  if (upper === 'CANCELLED_BY_HOST') return true;
  if (upper !== 'CONFIRMED') return false;
  const hoursUntilStart =
    (new Date(booking.session.startTime).getTime() - Date.now()) / (1000 * 60 * 60);
  return hoursUntilStart > MIN_CANCEL_HOURS;
}

/**
 * Check if a booking status represents a cancelled/refunded state
 */
export function isCancelledStatus(status: BookingStatusType): boolean {
  const upper = status.toUpperCase();
  return [
    'CANCELLED',
    'CANCELLED_CREDIT',
    'CANCELLED_REFUND',
    'CANCELLED_PASS',
    'REFUNDED',
  ].includes(upper);
}

/**
 * Check if a booking is in transferred state
 */
export function isTransferredStatus(status: string): boolean {
  return status.toUpperCase() === 'TRANSFERRED';
}

/**
 * Check if a booking can be transferred (confirmed, ≤24h before start but not yet started)
 */
export function canTransferBooking(booking: {
  status: string;
  session: { startTime: string };
}): boolean {
  if (booking.status.toUpperCase() !== 'CONFIRMED') return false;
  const hoursUntilStart =
    (new Date(booking.session.startTime).getTime() - Date.now()) / (1000 * 60 * 60);
  return hoursUntilStart > 0 && hoursUntilStart <= MIN_CANCEL_HOURS;
}

/**
 * Check if a booking is inactive (cancelled, refunded, or transferred)
 */
export function isInactiveStatus(status: BookingStatusType): boolean {
  return isCancelledStatus(status) || isTransferredStatus(status);
}

/**
 * Get hours remaining until activity starts
 */
export function getHoursUntilStart(startTime: string): number {
  return (new Date(startTime).getTime() - Date.now()) / (1000 * 60 * 60);
}
